import { Mic } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  getHandoverEntryBody,
  setHandoverEntryBody,
} from '../../utils/handoverNoteBody';
import EditableStringListCard from './EditableStringListCard';

function OutstandingTasksSection({ details = [], fallbackItems = [] }) {
  const items = details.length
    ? details
    : fallbackItems.map((title) => ({
        title,
        wasOverdue: false,
        dueTime: null,
      }));

  return (
    <div className="w-full">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-bold text-gray-900">Outstanding Tasks</h3>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-gray-500">No outstanding tasks.</p>
      ) : (
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={`${item.title}-${index}`} className="flex items-start gap-3 rounded-lg bg-gray-50 px-3 py-2">
              <span className="mt-0.5 h-4 w-4 rounded border-2 border-gray-300" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">{item.title}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {item.wasOverdue && (
                    <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-critical">
                      Overdue
                    </span>
                  )}
                  {item.dueTime && (
                    <span className="text-[11px] text-gray-500">
                      Was due at{' '}
                      {new Date(item.dueTime).toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PatientHandoverCard({ draft, onChange, onAddVoiceNote }) {
  const { t } = useLanguage();
  const body = getHandoverEntryBody(draft);

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
      <div className="flex items-start justify-between border-b border-gray-100 p-4">
        <div>
          <p className="font-bold text-gray-900" dir="auto">
            {draft.patientName}
          </p>
          <p className="text-sm text-gray-500">Room {draft.patientRoom || '—'}</p>
        </div>
        {draft.status === 'error' && (
          <span className="rounded-pill bg-critical-light px-2 py-0.5 text-xs font-semibold text-critical">
            Generation failed
          </span>
        )}
      </div>

      <div className="space-y-4 p-4">
        <textarea
          value={body}
          rows={10}
          placeholder={t('progressNotePlaceholder')}
          onChange={(event) => {
            onChange({
              ...draft,
              ...setHandoverEntryBody(draft, event.target.value),
            });
          }}
          className="w-full resize-y rounded-xl border-0 bg-gray-100 px-3 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30"
          aria-label={`${t('shiftHandover')} — ${draft.patientName}`}
          dir="auto"
        />

        <OutstandingTasksSection
          details={draft.outstanding_task_details || []}
          fallbackItems={draft.outstanding_tasks || []}
        />

        <EditableStringListCard
          title="Pending Results"
          items={draft.pending_results || []}
          onChange={(pending_results) => onChange({ ...draft, pending_results })}
          placeholder="No pending results."
        />

        <EditableStringListCard
          title="Medications Due Next Shift"
          items={draft.medications_due_next_shift || []}
          onChange={(medications_due_next_shift) => onChange({ ...draft, medications_due_next_shift })}
          placeholder="No medications due next shift."
        />

        <button
          type="button"
          onClick={onAddVoiceNote}
          className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/30 bg-primary-light/40 py-3 text-sm font-semibold text-primary"
        >
          <Mic className="h-4 w-4" />
          Add Voice Note
        </button>
      </div>
    </div>
  );
}
